import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QRCodeCanvas } from "qrcode.react";
import { Gift, Copy, Share2, Users, Sparkles, Loader2, QrCode, Download } from "lucide-react";

/**
 * ReferralCard — profile page widget for sharing your referral link.
 *
 * Shows the caller's `TBC-XXXXXX` code, a copy-able share link, a QR
 * code (downloadable as PNG for print/marketing), and a running total
 * of points earned + referrals completed. Backed by GET /api/referrals/me.
 */
export default function ReferralCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/referrals/me`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setStats(r.data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = async () => {
    if (!stats?.share_url) return;
    try {
      await navigator.clipboard.writeText(stats.share_url);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed — long-press the link to copy manually.");
    }
  };

  const nativeShare = async () => {
    if (!stats?.share_url) return;
    const shareData = {
      title: "Join The Blk Community",
      text: "Discover, support, and grow with Black-owned businesses.",
      url: stats.share_url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await copy();
      }
    } catch (e) {
      /* user cancelled */
    }
  };

  const downloadQR = () => {
    if (!qrRef.current || !stats) return;
    const canvas = qrRef.current.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `tbc-referral-${stats.code || "code"}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR downloaded — print it, share it, tape it on your storefront!");
  };

  if (loading) {
    return (
      <Card className="border-tbc-gold/20">
        <CardContent className="p-6 flex items-center gap-2 text-gray-500 font-body">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your referral link…
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Card
      className="border-tbc-gold/30 bg-gradient-to-br from-tbc-navy to-[#0a1426] text-white"
      data-testid="referral-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-white flex items-center gap-2">
          <Gift className="w-5 h-5 text-tbc-gold" />
          Refer a business — earn TBC Points
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-white/80 font-body leading-relaxed">
          Share your link with a Black-owned business. When they sign up, you
          both get <strong className="text-tbc-gold">50 pts</strong>. When
          they complete their first paid month, you earn{" "}
          <strong className="text-tbc-gold">100 pts</strong> more.
        </p>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <p className="text-2xl font-heading text-tbc-gold" data-testid="referral-count">
              {stats.total_referrals}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
              Referrals
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <p className="text-2xl font-heading text-tbc-gold">
              {stats.signup_points_earned + stats.activation_points_earned}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
              Pts earned
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <p className="text-2xl font-heading text-tbc-gold flex items-center justify-center gap-1">
              <Sparkles className="w-4 h-4" />
              {stats.code.slice(4)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
              Your code
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            readOnly
            value={stats.share_url}
            onClick={(e) => e.currentTarget.select()}
            className="bg-white/5 border-white/10 text-white text-sm font-mono"
            data-testid="referral-share-url"
          />
          <div className="flex gap-2">
            <Button
              onClick={copy}
              variant="outline"
              className="border-tbc-gold/50 text-tbc-gold hover:bg-tbc-gold/10 hover:text-tbc-gold"
              data-testid="referral-copy-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              onClick={() => setShowQR((v) => !v)}
              variant="outline"
              className="border-tbc-gold/50 text-tbc-gold hover:bg-tbc-gold/10 hover:text-tbc-gold"
              data-testid="referral-qr-toggle"
            >
              <QrCode className="w-4 h-4 mr-1.5" />
              {showQR ? "Hide" : "QR"}
            </Button>
            <Button
              onClick={nativeShare}
              className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
              data-testid="referral-share-btn"
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              Share
            </Button>
          </div>
        </div>

        {showQR && stats.share_url && (
          <div
            className="flex flex-col sm:flex-row items-center gap-4 p-4 bg-white rounded-xl mt-3"
            data-testid="referral-qr-block"
          >
            <div ref={qrRef} className="p-2 bg-white rounded-lg">
              <QRCodeCanvas
                value={stats.share_url}
                size={168}
                level="M"
                includeMargin={false}
                fgColor="#0a1e3f"
                bgColor="#ffffff"
              />
            </div>
            <div className="flex-1 min-w-0 text-tbc-navy">
              <p className="font-heading text-sm mb-1">Your personal QR code</p>
              <p className="text-xs text-gray-500 font-body leading-relaxed mb-3">
                Anyone who scans this and signs up counts toward your
                referral points. Add it to your storefront window,
                business card, flyers, or social posts.
              </p>
              <Button
                onClick={downloadQR}
                size="sm"
                className="bg-tbc-navy text-white hover:bg-tbc-navy/90"
                data-testid="referral-qr-download"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Download PNG
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs text-white/50 font-body flex items-center gap-1.5">
          <Users className="w-3 h-3" />
          Points appear in your wallet the moment they sign up.
        </p>
      </CardContent>
    </Card>
  );
}
